import { renderableSlideIndices } from '@digital-twins/deck-ui/slides/slide-retirement'
import type { Deck, SlideData } from '@digital-twins/deck-ui/slides/contracts'
import { resolveCoverBackgroundUrl } from '@digital-twins/deck-ui/foundations/cover_backgrounds'
import { resolveLegacyFairgenLogoUrl } from '@/lib/theme/fairgen-default-brand'

interface PrepareDeckForRenderOptions {
  coverBackgroundSeed?: string
  /** Legacy fallback logo URL when deck metadata and slide data do not carry one. */
  clientLogoUrl?: string | null
}

const DARK_LOGO_SURFACE_SLIDE_TYPES = new Set(['cover', 'outro'])

function resolveCoverBackground(
  deck: Deck,
  fallbackSeed?: string,
): string | undefined {
  const seed = fallbackSeed || deck.id || deck.experimentId || deck.title
  return resolveCoverBackgroundUrl(deck.metadata?.coverImageUrl, seed)
}

export function prepareDeckForRender(
  deck: Deck,
  options: PrepareDeckForRenderOptions = {},
): Deck {
  const sourceIndices = renderableSlideIndices(deck.slides)
  if (sourceIndices.length !== deck.slides.length) {
    const hidden = deck.metadata?.hiddenSlideIndices
    deck = {
      ...deck,
      slides: sourceIndices.map((index) => deck.slides[index]),
      ...(hidden ? { metadata: { ...deck.metadata,
        hiddenSlideIndices: sourceIndices.flatMap((sourceIndex, renderIndex) => hidden.includes(sourceIndex) ? [renderIndex] : []),
      } } : {}),
    }
  }
  const coverImageUrl = resolveCoverBackground(deck, options.coverBackgroundSeed)
  const metadataLogoUrl = resolveLegacyFairgenLogoUrl(deck.metadata?.brandLogoUrl)
  const metadataLogoUrlOnDark = resolveLegacyFairgenLogoUrl(deck.metadata?.brandLogoUrlOnDark)
  const fallbackLogoUrl = options.clientLogoUrl ?? null

  const deckWithCoverMetadata =
    !coverImageUrl || coverImageUrl === deck.metadata?.coverImageUrl
      ? deck
      : {
          ...deck,
          metadata: {
            ...deck.metadata,
            coverImageUrl,
          },
        }

  if (!coverImageUrl && !metadataLogoUrl && !metadataLogoUrlOnDark && !fallbackLogoUrl) {
    return deckWithCoverMetadata
  }

  const slides = deckWithCoverMetadata.slides.map((slide): SlideData => {
    const withCover =
      coverImageUrl && slide.type === 'cover'
        ? ({ ...slide, heroImage: coverImageUrl } as SlideData)
        : slide
    if (metadataLogoUrl || metadataLogoUrlOnDark) {
      const slideLogoUrl = DARK_LOGO_SURFACE_SLIDE_TYPES.has(slide.type)
        ? (metadataLogoUrlOnDark || metadataLogoUrl)
        : (metadataLogoUrl || metadataLogoUrlOnDark)

      return { ...withCover, clientLogoUrl: slideLogoUrl } as SlideData
    }
    if (fallbackLogoUrl && !withCover.clientLogoUrl) {
      return { ...withCover, clientLogoUrl: fallbackLogoUrl } as SlideData
    }
    return withCover
  })

  return { ...deckWithCoverMetadata, slides }
}
